import { useState } from 'react';
import {
    BodyLargeRegular,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    H5Emphasized,
    InfoButton,
    SelectTile,
    UserIcon,
    UsersIcon,
    XStack,
    YStack,
} from 'smoothie';

import type { User } from '../../../../../user/types/User';
import { AccountOwnership } from '../../../../types/BankAccount';

interface SelectOwnershipProps {
    partnerMember?: Partial<User> | null;
    onPressNext: (ownership: AccountOwnership) => void;
    onInfoButtonPress: () => void;
}

const SelectOwnership = ({
    partnerMember,
    onPressNext,
    onInfoButtonPress,
}: SelectOwnershipProps) => {
    const [ownership, setOwnership] = useState<AccountOwnership | null>(null);
    const jointSelectionDisabled = partnerMember?.email == null;

    return (
        <YStack gap={24} justifyContent="space-between">
            <YStack gap={24}>
                <XStack justifyContent="space-between" alignItems="center">
                    <H5Emphasized>Who is this account for?</H5Emphasized>
                    <InfoButton onPress={onInfoButtonPress} />
                </XStack>
                <YStack gap={16}>
                    <SelectTile
                        title="Just me"
                        description="Individual account"
                        icon={UserIcon}
                        isSelected={ownership === AccountOwnership.INDIVIDUAL}
                        onPress={() => {
                            setOwnership(AccountOwnership.INDIVIDUAL);
                        }}
                    />
                    <SelectTile
                        title="Me and my partner"
                        description="Joint account"
                        icon={UsersIcon}
                        isSelected={ownership === AccountOwnership.JOINT}
                        onPress={() => {
                            setOwnership(AccountOwnership.JOINT);
                        }}
                        disabled={jointSelectionDisabled}
                    />
                    <YStack paddingHorizontal={16}>
                        {jointSelectionDisabled && (
                            <YStack gap={8}>
                                <BodyMediumRegular color="$gray1000">
                                    You need a partner to open a joint Fruitful
                                    Cash Account.
                                </BodyMediumRegular>
                                <BodyMediumRegular color="$gray1000">
                                    Please reach out to support@fruitful.com if
                                    you&apos;d like to update your membership
                                </BodyMediumRegular>
                            </YStack>
                        )}
                        {ownership === AccountOwnership.JOINT && (
                            <YStack gap={12} marginTop={12}>
                                <BodyLargeRegular>
                                    An invite will be sent to &quot;
                                    {partnerMember?.email ?? ''}&quot;
                                </BodyLargeRegular>
                                <BodyMediumRegular color="$gray1100">
                                    It will include instructions for joining
                                    Fruitful. Once we verify their identity,
                                    they&apos;ll get shared access to this
                                    account.
                                </BodyMediumRegular>
                            </YStack>
                        )}
                    </YStack>
                </YStack>
            </YStack>

            <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.LARGE}
                text="Open account"
                onPress={() => {
                    if (ownership != null) onPressNext(ownership);
                }}
                disabled={ownership == null}
            />
        </YStack>
    );
};

export default SelectOwnership;
